// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { API_URL } from 'Config';
import { postJson, patchJson, getJson } from 'Utils/http';
import { clearLocalStorage, getHTML, setHTML, getLoginFailedAttempts, getPlanDetails, getToken, getUser, setToken, setUser } from 'Utils/localStorageService';
import { Dispatch } from 'redux';
import history from 'Utils/history';
import jwtDecode from 'jwt-decode';
import { clearSessionStorage } from 'Utils/sessionStorageService';
import { ADMIN_ROUTE, EXPIRED_TOKEN_ROUTE, PROJECTS_ROUTE, ROOT_ROUTE, isPublicAuthPath } from 'Utils/routes';
import { sendForgotPasswordLink } from './authSlice';
import { InitAuthFailedAction, InitAuthSuccessAction, LogoutAction, OktaAdminLoginStartAction, OktaAdminLoginSuccessAction, OnAcceptTnCAction } from './authActions';
import { reloadPage } from './reloadPage';
import { rememberSignedOutNotice } from './signedOutNotice';

export const InitAuthThunk = () => (dispatch: Dispatch) => {
  const currentLocation = history.location.pathname;
  let user;
  if (!_ADMIN_ && !isPublicAuthPath(currentLocation)) {
    user = ReadFromStorage()(dispatch);
  }

  if (!user) return;

  if (currentLocation === ROOT_ROUTE) {
    history.push(_ADMIN_ ? ADMIN_ROUTE : PROJECTS_ROUTE);
  }
};

export const ReadFromStorage = () => (dispatch: Dispatch) => {
  const failedLoginAttempts = +getLoginFailedAttempts();
  try {
    const userData = getUser();
    const user = JSON.parse(userData);
    const html = getHTML();
    const token = getToken();
    const tokenExpires = jwtDecode<{ exp: number }>(token).exp;
    const { resources } = jwtDecode<{ resources: any}>(token);
    const planDetails = getPlanDetails();
    dispatch(InitAuthSuccessAction({ user, token, tokenExpires, html, resources, planDetails, failedLoginAttempts }));
    return user;
  } catch {
    clearLocalStorage();
    // Don't redirect if we're on the Auth0 callback route
    if (history.location.pathname !== '/callback') {
      history.push('/');
    }
    dispatch(InitAuthFailedAction(failedLoginAttempts));
    return false;
  }
};

export const OktaAdminLoginThunk = (tokens: any, pricingAccess?: boolean) => async (
  dispatch: Dispatch,
) => {
  const decodedToken = jwtDecode<{ exp: number, sub: string }>(tokens.accessToken);
  dispatch(OktaAdminLoginStartAction());
  const user = { email: decodedToken.sub, roles: ['admin'], pricingAccess };

  setUser(JSON.stringify(user));
  setToken(tokens.accessToken);

  dispatch(
    OktaAdminLoginSuccessAction({
      user,
      token: tokens.accessToken,
      tokenExpires: decodedToken.exp,
      resources: ['editorFunctions'],
    }),
  );
};

export const ForgotPasswordThunk = (data: { email: string, captchaToken?: string }) => async (dispatch: Dispatch) => {
  const url = `${API_URL}/forgotPassword`;
  const result = await postJson<{ message: string }>(url, data);
  dispatch(sendForgotPasswordLink(result.success));
  return result;
};

export const UpdateUserTnCDateThunk = () =>
  async (dispatch: Dispatch, getState: () => IState) => {
    const { user } = getState().auth;
    const url = `${API_URL}/updateUserTnCDate/${user._id}`;
    const { token } = getState().auth;

    const result = await patchJson<IUserResponse>(url, user, token);
    if (result.success) {
      dispatch(OnAcceptTnCAction(result.data));
      setHTML(null);
    }

    return result;
  };

export const VerifyTokenThunk = (token: string) => async () => {
  const url = `${API_URL}/verifyToken/${token}`;
  const result = await getJson<{ email: string, jwt: string }>(url, token);
  if (result.success) {
    setToken(result.data.jwt);
    return result.data.jwt;
  }
  history.push(EXPIRED_TOKEN_ROUTE);
  return null;
};

export const ResetPasswordThunk = (payload: { password: string }) => async () => {
  const url = `${API_URL}/passwordReset`;
  const token = getToken();
  const data = { token, password: payload.password };
  const result = await postJson<{ message: string }>(url, data);
  if (result.success) {
    history.push(ROOT_ROUTE);
    clearLocalStorage();
  } else {
    return { message: 'something went wrong' };
  }
  return null;
};

const LOGOUT_TIMEOUT_MS = 3000;

type LogoutOptions = { leave?: () => void | Promise<void> };

const isTokenLive = (token: string) => {
  try {
    return jwtDecode<{ exp: number }>(token).exp > Date.now() / 1000;
  } catch {
    return false;
  }
};

// The server forgets this browser's session token and refuses it from then on; the user's other browsers and
// devices keep theirs. A slow or failed answer holds the sign-out up for no longer than the timeout.
const EndServerSession = async (token: string) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), LOGOUT_TIMEOUT_MS);
  try {
    await postJson<{ message: string }>(`${API_URL}/users/logout`, undefined, token, controller.signal);
  } catch {
    // The session is already over in this browser, and an unrevoked token still expires on its own
  } finally {
    clearTimeout(timeout);
  }
};

// The session is cleared before the server is asked, so every other tab of the browser signs out at once
// (useCrossTabSignOut). The page leaves only once the server has answered or the timeout has passed, so leaving
// never cuts the server's sign-out off: by a reload, or by `leave` (Auth0's sign-out for an SSO customer). An
// expired token is not sent; the server would only refuse it.
export const Logout = ({ leave = reloadPage }: LogoutOptions = {}) => async (dispatch: Dispatch) => {
  const token = getToken();
  clearLocalStorage();
  clearSessionStorage();
  dispatch(LogoutAction());
  history.push(ROOT_ROUTE);
  if (token && !_ADMIN_ && isTokenLive(token)) {
    await EndServerSession(token);
  }
  try {
    await leave();
  } catch {
    reloadPage();
  }
};

// Another tab of this browser signed out, which ended the session this tab shared with it. The tab reloads
// without changing anything first: a sign-in page drawn before the reload would use up the note, and the reloaded
// app finds no session and opens the sign-in page itself.
export const EndSessionFromOtherTab = () => () => {
  clearSessionStorage();
  rememberSignedOutNotice();
  reloadPage();
};
